import type { AccountProfile } from '../../api/models';
import { AppText } from '../../components/AppText';
import { AppTextField } from '../../components/AppTextField';
import { InlineNotice } from '../../components/InlineNotice';
import { PrimaryButton } from '../../components/PrimaryButton';
import { ProviderButtons } from '../../components/ProviderButtons';
import { Strings } from '../../strings';

import { canSubmit } from './SignInState';
import { useSignIn } from './useSignIn';

export function SignInScreen({
  notice,
  initialEmail = '',
  onSignedIn,
  onForgotPassword,
  onCreateAccount,
  onBackToIntro,
}: {
  /** Carried over from registration, so the parent does not retype what they typed seconds ago (R0080). */
  initialEmail?: string;
  notice?: string | null;
  onSignedIn: (account: AccountProfile) => void;
  onForgotPassword: () => void;
  /**
   * Sign-in is where the app sends people — every unrecognised address and a lost child session land
   * here, not on Welcome. Without these two a visitor who has never registered can reach this screen and
   * has no route to registration at all (R0085).
   */
  onCreateAccount: () => void;
  onBackToIntro: () => void;
}) {
  const model = useSignIn(onSignedIn, initialEmail);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void model.submit();
      }}
      style={{ display: 'flex', flexDirection: 'column', padding: '0 var(--space-6)' }}
    >
      <AppText as="h1" style="display">
        {Strings.appName}
      </AppText>

      <AppText
        color="var(--text-secondary)"
        sx={{ marginTop: 'var(--space-2)', marginBottom: 'var(--space-8)' }}
      >
        {Strings.signInSubtitle}
      </AppText>

      {/* A one-off message from wherever sent the parent here — after registering, most often. */}
      {notice ? (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <InlineNotice text={notice} tone="info" />
        </div>
      ) : null}

      {model.state.error !== null ? (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <InlineNotice text={model.state.error} tone="error" />
        </div>
      ) : null}

      <AppTextField
        label={Strings.labelEmail}
        value={model.state.email}
        onChange={model.onEmailChange}
        inputMode="email"
        type="email"
        autoComplete="email"
      />

      <div style={{ marginTop: 'var(--space-4)' }}>
        <AppTextField
          label={Strings.labelPassword}
          value={model.state.password}
          onChange={model.onPasswordChange}
          isPassword
          autoComplete="current-password"
        />
      </div>

      <div style={{ marginTop: 'var(--space-8)' }}>
        <PrimaryButton
          type="submit"
          label={model.state.isSubmitting ? Strings.actionSigningIn : Strings.actionSignIn}
          enabled={canSubmit(model.state)}
          loading={model.state.isSubmitting}
        />
      </div>

      <ProviderButtons label={Strings.signInWithProvider} />

      <button
        type="button"
        onClick={onForgotPassword}
        style={{
          marginTop: 'var(--space-4)',
          marginBottom: 'var(--space-2)',
          background: 'none',
          border: 'none',
          padding: 0,
          textAlign: 'left',
          color: 'var(--action-primary)',
          fontSize: '15px',
          lineHeight: '20px',
          fontWeight: 600,
        }}
      >
        {Strings.actionForgotPassword}
      </button>

      <button type="button" onClick={onCreateAccount} style={LINK_STYLE}>
        {Strings.actionCreateAccount}
      </button>

      <button type="button" onClick={onBackToIntro} style={LINK_STYLE}>
        {Strings.actionBackToIntro}
      </button>
    </form>
  );
}

const LINK_STYLE = {
  marginBottom: 'var(--space-2)',
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'left',
  color: 'var(--action-primary)',
  fontSize: '15px',
  lineHeight: '20px',
  fontWeight: 600,
} as const;
